import * as fs from "node:fs";
import * as path from "node:path";
import { isAbsolute, resolve } from "node:path";
import {
	Container,
	type Focusable,
	getKeybindings,
	type Input,
	Key,
	matchesKey,
	Spacer,
	Text,
} from "@caupulican/pi-tui";
import {
	decodeResourceSelection,
	detectResourceFraming,
	encodeResourceSelectionWithFraming,
	type ResourceFraming,
	remapResourceSelectionFilter,
} from "../../../core/profile-resource-selection.ts";
import {
	matchesResourceProfilePattern,
	type ResourceProfileFilterSettings,
	type ResourceProfileKind,
	type ResourceProfileSettings,
} from "../../../core/settings-manager.ts";
import { theme } from "../theme/theme.ts";
import { DynamicBorder } from "./dynamic-border.ts";
import { keyText } from "./keybinding-hints.ts";
import {
	clearSelectorSearchOrCancel,
	formatDirtySelectorFooter,
	SearchableListSurface,
	SelectorListState,
	SelectorStatusFooter,
} from "./selector-list.ts";

export interface ProfileResourceItem {
	/** Collision-safe value persisted into the profile filter. */
	id: string;
	/** Human-facing name; defaults to id. */
	label?: string;
	path?: string;
	description?: string;
}

export interface ProfileResourceEditorKind {
	kind: ResourceProfileKind; // "tools" | "skills" | "extensions" | "agents" | "prompts" | "themes"
	label: string; // display label, e.g. "Tools"
	items: ProfileResourceItem[]; // the available items of this kind
}

export interface ProfileResourceEditorOptions {
	profileName: string;
	profileScope: string; // e.g. "directory" | "project" | "global" | "session" | "reusable-file"
	initialResources: ResourceProfileSettings; // existing profile.resources; may be {}
	kinds: ProfileResourceEditorKind[]; // the six kinds, with their universes
	onSave: (resources: ResourceProfileSettings) => void; // called on ctrl+s with the encoded result
	onCancel: () => void; // called on esc
	onScopeChange?: () => void;
	onEdit?: (id: string, path: string, kind: ResourceProfileKind) => void;
	cwd?: string;
	agentDir?: string;
	externalResourceRoots?: string[];
}

interface ResourceItem {
	id: string;
	label: string;
	enabled: boolean;
	path?: string;
	description?: string;
	sourceLabel?: "catalog" | "user" | "project" | "bundled";
	isMissing?: boolean;
}

const TOOL_DESCRIPTIONS: Record<string, string> = {
	read: "Read files from the filesystem.",
	bash: "Execute arbitrary commands in the shell.",
	edit: "Preflight and edit existing files surgically.",
	write: "Preflight and create new files without overwriting.",
	grep: "Search for text patterns using ripgrep.",
	find: "Locate files matching a search query.",
	ls: "List files and directories.",
	skill_audit: "Inspect and audit local skills.",
	skillify: "Convert a set of files/instructions into a skill.",
	extensionify: "Package a tool or script as an extension.",
};

export function resolveResourceEditPath(
	_id: string,
	filePath: string | undefined,
	kind: ResourceProfileKind,
): string | undefined {
	if (kind === "tools") {
		return undefined;
	}

	if (!filePath) {
		return undefined;
	}

	if (kind === "extensions") {
		try {
			const stats = fs.statSync(filePath);
			if (stats.isDirectory()) {
				const indexTs = path.join(filePath, "index.ts");
				if (fs.existsSync(indexTs)) {
					return indexTs;
				}
				const indexJs = path.join(filePath, "index.js");
				if (fs.existsSync(indexJs)) {
					return indexJs;
				}
				const pkgJsonPath = path.join(filePath, "package.json");
				if (fs.existsSync(pkgJsonPath)) {
					try {
						const pkg = JSON.parse(fs.readFileSync(pkgJsonPath, "utf-8"));
						if (typeof pkg.main === "string") {
							const resolvedMain = path.resolve(filePath, pkg.main);
							if (fs.existsSync(resolvedMain)) {
								return resolvedMain;
							}
						}
					} catch {}
				}
				return indexTs;
			}
		} catch {}
		return filePath;
	}

	if (kind === "skills") {
		try {
			const stats = fs.statSync(filePath);
			if (stats.isDirectory()) {
				const skillMd = path.join(filePath, "SKILL.md");
				if (fs.existsSync(skillMd)) {
					return skillMd;
				}
			}
		} catch {}
		return filePath;
	}

	return filePath;
}

export function classifyResourceSource(
	filePath: string | undefined,
	cwd: string,
	agentDir: string,
	externalRoots: string[],
): "catalog" | "user" | "project" | "bundled" {
	if (!filePath) {
		return "bundled";
	}

	const absolutePath = isAbsolute(filePath) ? filePath : resolve(cwd, filePath);
	const normPath = resolve(absolutePath);
	const normCwd = resolve(cwd);
	const normAgentDir = agentDir ? resolve(agentDir) : "";

	for (const extRoot of externalRoots) {
		const normExt = resolve(extRoot);
		if (normPath.startsWith(normExt)) {
			return "catalog";
		}
	}

	if (normAgentDir && normPath.startsWith(normAgentDir)) {
		return "user";
	}

	if (normPath.startsWith(normCwd)) {
		return "project";
	}

	if (normPath.includes("node_modules")) {
		return "bundled";
	}

	return "bundled";
}

export class ProfileResourceEditorComponent extends Container implements Focusable {
	private profileName: string;
	private profileScope: string;
	private kinds: ProfileResourceEditorKind[];
	private enabledByKind: Map<ResourceProfileKind, Set<string>> = new Map();
	private framingByKind: Map<ResourceProfileKind, ResourceFraming> = new Map();
	private missingIdsByKind: Map<ResourceProfileKind, Set<string>> = new Map();
	/** The filter each kind was decoded from, so an all-enabled save preserves an enumerated grant
	 *  (or an already-wildcard grant) instead of widening it to allow:["*"]. */
	private originalFilterByKind: Map<ResourceProfileKind, ResourceProfileFilterSettings | undefined> = new Map();
	private currentKindIndex = 0;

	private cwd: string;
	private agentDir: string;
	private externalResourceRoots: string[];

	private readonly listState = new SelectorListState<ResourceItem>(() => this.updateList());
	private searchInput: Input;
	private kindHeaderText: Text;
	private listContainer: Container;
	private descriptionText: Text;
	private footerText: Text;
	private searchSurface: SearchableListSurface;
	private isDirty = false;
	private maxVisible = 8;

	get focused(): boolean {
		return this.searchSurface.focused;
	}
	set focused(value: boolean) {
		this.searchSurface.focused = value;
	}

	private onSave: (resources: ResourceProfileSettings) => void;
	private onCancel: () => void;
	private onScopeChange?: () => void;
	private onEdit?: (id: string, path: string, kind: ResourceProfileKind) => void;

	constructor(options: ProfileResourceEditorOptions) {
		super();
		this.profileName = options.profileName;
		this.profileScope = options.profileScope;
		this.kinds = options.kinds;
		this.onSave = options.onSave;
		this.onCancel = options.onCancel;
		this.onScopeChange = options.onScopeChange;
		this.onEdit = options.onEdit;
		this.cwd = options.cwd || process.cwd();
		this.agentDir = options.agentDir || "";
		this.externalResourceRoots = options.externalResourceRoots || [];

		// Initialize enabled, framing, and missing sets for each kind
		for (const kind of this.kinds) {
			const filter = options.initialResources[kind.kind];
			const remapped = remapResourceSelectionFilter(filter, kind.items, (item, pattern) => {
				if (item.id === pattern) return true;
				return item.path ? matchesResourceProfilePattern(item.path, [pattern], this.cwd) : false;
			});
			this.missingIdsByKind.set(kind.kind, remapped.missingIds);

			// Decode against concrete collision-safe ids plus genuinely missing profile entries. Legacy
			// basename/parent/glob patterns are expanded to the resources they currently match, so saving
			// migrates ambiguous patterns such as "index.ts" instead of retaining a multi-extension toggle.
			const allIds = kind.items.map((item) => item.id);
			const enabledSet = decodeResourceSelection(remapped.filter, [...allIds, ...remapped.missingIds]);
			this.enabledByKind.set(kind.kind, enabledSet);
			this.framingByKind.set(kind.kind, detectResourceFraming(filter));
			this.originalFilterByKind.set(kind.kind, filter);
		}

		// Header
		this.addChild(new DynamicBorder());
		this.addChild(
			new Text(
				theme.fg("accent", theme.bold(`Library — editing "${this.profileName}" (${this.profileScope})`)),
				0,
				0,
			),
		);
		this.addChild(
			new Text(
				theme.fg(
					"muted",
					`Navigate kinds: ${keyText("tui.input.tab")}. Toggle: ${keyText("tui.select.confirm")}. Mode: ${theme.fg("accent", "a")} allow / ${theme.fg("accent", "b")} block. Scope: ${theme.fg("accent", "s")} change. Edit: ${theme.fg("accent", "e")}. Bulk: ${keyText("app.profiles.enableAll")} enable all / ${keyText("app.profiles.clearAll")} clear all.`,
				),
				0,
				0,
			),
		);
		this.addChild(new Spacer(1));

		// Kind selector header
		this.kindHeaderText = new Text(this.getKindHeaderText(), 0, 0);
		this.addChild(this.kindHeaderText);
		this.addChild(new Spacer(1));

		this.searchSurface = SearchableListSurface.mount(this);
		this.searchInput = this.searchSurface.searchInput;
		this.listContainer = this.searchSurface.listContainer;

		this.descriptionText = new Text("", 0, 0);
		const statusFooter = new SelectorStatusFooter(this.getFooterText(), this.descriptionText);
		this.footerText = statusFooter.footerText;
		this.addChild(statusFooter);
		this.refresh();
	}

	private getKindHeaderText(): string {
		const kind = this.kinds[this.currentKindIndex]!;
		const enabledSet = this.enabledByKind.get(kind.kind)!;
		const framing = this.framingByKind.get(kind.kind)!;
		const framingText = framing === "allow" ? "allow-list" : "block-list";
		const countText = `${enabledSet.size}/${kind.items.length} enabled (${framingText})`;
		const kindIndicator = this.kinds
			.map((k, i) => {
				const marker = i === this.currentKindIndex ? "●" : "○";
				return theme.fg(i === this.currentKindIndex ? "accent" : "muted", `${marker} ${k.label}`);
			})
			.join(" ");
		return `${kindIndicator}  ${theme.fg("muted", countText)}`;
	}

	private getCurrentKind(): ProfileResourceEditorKind {
		return this.kinds[this.currentKindIndex]!;
	}

	private getCurrentEnabledSet(): Set<string> {
		const kind = this.getCurrentKind();
		return this.enabledByKind.get(kind.kind)!;
	}

	private buildItems(): ResourceItem[] {
		const kind = this.getCurrentKind();
		const enabledSet = this.getCurrentEnabledSet();

		const items: ResourceItem[] = [];

		// Add all available items
		for (const availableItem of kind.items) {
			const isEnabled = enabledSet.has(availableItem.id);
			let desc = availableItem.description;
			if (kind.kind === "tools" && !desc) {
				desc = TOOL_DESCRIPTIONS[availableItem.id];
			}
			items.push({
				id: availableItem.id,
				label: availableItem.label ?? availableItem.id,
				enabled: isEnabled,
				path: availableItem.path,
				description: desc,
				sourceLabel: classifyResourceSource(
					availableItem.path,
					this.cwd,
					this.agentDir,
					this.externalResourceRoots,
				),
				isMissing: false,
			});
		}

		// Add missing items
		const missingSet = this.missingIdsByKind.get(kind.kind) || new Set<string>();
		for (const missingId of missingSet) {
			const isEnabled = enabledSet.has(missingId);
			items.push({
				id: missingId,
				label: missingId,
				enabled: isEnabled,
				isMissing: true,
				description: "Referenced in profile but missing from available resources.",
			});
		}

		// Sort: enabled first, then disabled
		const enabled: ResourceItem[] = [];
		const disabled: ResourceItem[] = [];
		for (const item of items) {
			if (item.enabled) {
				enabled.push(item);
			} else {
				disabled.push(item);
			}
		}
		return [...enabled, ...disabled];
	}

	private getFooterText(): string {
		const kind = this.getCurrentKind();
		const enabledSet = this.getCurrentEnabledSet();
		const countText = `${enabledSet.size}/${kind.items.length} enabled`;
		const parts = [
			`${keyText("tui.select.confirm")} toggle`,
			`${keyText("tui.input.tab")} kind`,
			`${keyText("app.models.save")} save`,
			`${theme.fg("accent", "e")} edit`,
			`${theme.fg("accent", "Ctrl+Q")}/${keyText("app.interrupt")} cancel`,
			countText,
		];
		return formatDirtySelectorFooter(parts, this.isDirty);
	}

	private refresh(): void {
		this.listState.refresh(
			this.buildItems(),
			this.searchInput.getValue(),
			(item) => `${item.label} ${item.id} ${item.description ?? ""}`,
		);
		this.updateList();
		this.footerText.setText(this.getFooterText());
	}

	private updateList(): void {
		this.listContainer.clear();

		const kind = this.getCurrentKind();
		const missingSet = this.missingIdsByKind.get(kind.kind) || new Set<string>();
		const totalAvailableCount = kind.items.length + missingSet.size;

		if (totalAvailableCount === 0) {
			this.listContainer.addChild(
				new Text(theme.fg("muted", "  (none available — add a Source or install resources)"), 0, 0),
			);
			this.descriptionText.setText(theme.fg("muted", "  No description available"));
			return;
		}

		if (this.listState.items.length === 0) {
			this.listContainer.addChild(new Text(theme.fg("muted", "  No matching resources"), 0, 0));
			this.descriptionText.setText(theme.fg("muted", "  No description available"));
			return;
		}

		const range = this.listState.visitVisible(this.maxVisible, (item, _index, isSelected) => {
			const prefix = isSelected ? theme.fg("accent", "→ ") : "  ";
			let resourceText = isSelected ? theme.fg("accent", item.label) : item.label;

			if (item.isMissing) {
				resourceText = theme.fg("muted", `${item.label} [missing]`);
			} else if (item.sourceLabel) {
				const labelColor =
					item.sourceLabel === "catalog"
						? "thinkingText"
						: item.sourceLabel === "project"
							? "success"
							: item.sourceLabel === "user"
								? "warning"
								: "muted";
				const labelText = theme.fg(labelColor, ` [${item.sourceLabel}]`);
				resourceText = `${resourceText}${labelText}`;
			}

			const status = item.enabled ? theme.fg("success", " ✓") : theme.fg("dim", " ✗");
			this.listContainer.addChild(new Text(`${prefix}${resourceText}${status}`, 0, 0));
		});

		this.listState.appendScrollInfo(this.listContainer, range);

		// Update description area for current selection
		const selectedItem = this.listState.items[this.listState.selectedIndex];
		if (selectedItem?.description) {
			this.descriptionText.setText(theme.fg("muted", `  Description: ${selectedItem.description}`));
		} else if (selectedItem && selectedItem.label !== selectedItem.id) {
			this.descriptionText.setText(theme.fg("muted", `  Resource: ${selectedItem.id}`));
		} else {
			this.descriptionText.setText(theme.fg("muted", "  No description available"));
		}
	}

	handleInput(data: string): void {
		const kb = getKeybindings();
		if (this.listState.handleNavigation(data, "wrap", kb)) return;

		// Switch kind with Tab (cycles forward)
		if (kb.matches(data, "tui.input.tab")) {
			this.currentKindIndex = (this.currentKindIndex + 1) % this.kinds.length;
			this.listState.selectedIndex = 0;
			this.searchInput.setValue("");
			this.kindHeaderText.setText(this.getKindHeaderText());
			this.refresh();
			return;
		}

		// Switch framing: Allow-list
		if (matchesKey(data, Key.ctrl("a")) || (this.searchInput.getValue() === "" && data === "a")) {
			this.framingByKind.set(this.getCurrentKind().kind, "allow");
			this.isDirty = true;
			this.kindHeaderText.setText(this.getKindHeaderText());
			this.refresh();
			return;
		}

		// Switch framing: Block-list
		if (matchesKey(data, Key.ctrl("b")) || (this.searchInput.getValue() === "" && data === "b")) {
			this.framingByKind.set(this.getCurrentKind().kind, "block");
			this.isDirty = true;
			this.kindHeaderText.setText(this.getKindHeaderText());
			this.refresh();
			return;
		}

		// Edit highlighted resource
		if (data === "e" && this.searchInput.getValue() === "") {
			const item = this.listState.items[this.listState.selectedIndex];
			if (item && !item.isMissing && item.path) {
				const kind = this.getCurrentKind().kind;
				if (this.onEdit) {
					this.onEdit(item.id, item.path, kind);
				}
			}
			return;
		}

		// Switch scope: Scope selector
		if (matchesKey(data, Key.ctrl("o")) || (this.searchInput.getValue() === "" && data === "s")) {
			if (this.onScopeChange) {
				this.onScopeChange();
			}
			return;
		}

		// Toggle on space/enter
		if (kb.matches(data, "tui.select.confirm")) {
			const item = this.listState.items[this.listState.selectedIndex];
			if (item) {
				const enabledSet = this.getCurrentEnabledSet();
				if (enabledSet.has(item.id)) {
					enabledSet.delete(item.id);
				} else {
					enabledSet.add(item.id);
				}
				this.isDirty = true;
				this.refresh();
			}
			return;
		}

		// Save/persist to settings
		if (kb.matches(data, "app.models.save")) {
			this.persistChanges();
			return;
		}

		// Ctrl+C - clear search or cancel if empty
		if (matchesKey(data, Key.ctrl("c"))) {
			clearSelectorSearchOrCancel(this.searchInput, () => this.refresh(), this.onCancel);
			return;
		}

		// Escape / Ctrl+Q - cancel (unconditional)
		if (
			kb.matches(data, "app.interrupt") ||
			matchesKey(data, Key.escape) ||
			matchesKey(data, Key.esc) ||
			matchesKey(data, "ctrl+q")
		) {
			this.onCancel();
			return;
		}

		// Bulk actions: enable/clear every currently-listed item (the filtered subset when a search
		// query is active, otherwise the whole kind including missing entries).
		if (kb.matches(data, "app.profiles.enableAll")) {
			const enabledSet = this.getCurrentEnabledSet();
			for (const item of this.listState.items) {
				enabledSet.add(item.id);
			}
			this.isDirty = true;
			this.kindHeaderText.setText(this.getKindHeaderText());
			this.refresh();
			return;
		}
		if (kb.matches(data, "app.profiles.clearAll")) {
			const enabledSet = this.getCurrentEnabledSet();
			for (const item of this.listState.items) {
				enabledSet.delete(item.id);
			}
			this.isDirty = true;
			this.kindHeaderText.setText(this.getKindHeaderText());
			this.refresh();
			return;
		}

		// Pass everything else to search input
		this.searchInput.handleInput(data);
		this.refresh();
	}

	private persistChanges(): void {
		const resources: ResourceProfileSettings = {};
		for (const kind of this.kinds) {
			const enabledSet = this.enabledByKind.get(kind.kind)!;
			const framing = this.framingByKind.get(kind.kind)!;
			// Persist against the same widened universe used at decode time: profile-referenced
			// ids that are not currently discoverable must survive a save untouched.
			const missingSet = this.missingIdsByKind.get(kind.kind) ?? new Set<string>();
			const allIds = [...new Set([...kind.items.map((item) => item.id), ...missingSet])];
			// Pass the original filter so an all-enabled save preserves an enumerated grant (and only
			// keeps a wildcard when the grant was already a wildcard) — never widening it silently.
			const encoded = encodeResourceSelectionWithFraming(enabledSet, allIds, framing, {
				originalFilter: this.originalFilterByKind.get(kind.kind),
			});
			if (encoded !== undefined) {
				resources[kind.kind] = encoded;
			}
		}
		this.onSave(resources);
		this.isDirty = false;
		this.footerText.setText(this.getFooterText());
	}

	getSearchInput(): Input {
		return this.searchInput;
	}
}
